/**
 * Connection Detail Pane
 *
 * Shows detailed information about a selected data connection.
 * Follows the same design patterns as the candidate detail pane.
 */

import React, { useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  LinkIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  useGetConnector,
  useListSyncRuns,
  useGetConnectorStatistics,
  useTriggerSync,
  useDeleteConnector,
  useUpdateConnector,
} from '../../generated/connectors/connectors';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/query-keys';
import { useToasts } from '../../stores/useToasts';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { googleWorkspaceAuthorizeOauthAuthorizeGoogleGet } from '../../generated/oauth/oauth';
import { cn } from '../../shared/lib/cn';
import { format } from 'date-fns';
import {
  formatConnectorActivityTime,
  formatRelativeApiTime,
  parseApiDate,
} from './dateUtils';
import {
  getConnectorCatalogEntry,
  getConnectorLabel,
  getConnectorRequiredPermissions,
  getConnectorScopeLabel,
  getConnectorSummary,
} from './connectorCatalog';
import {
  getDataConnectionCategory,
  getDataConnectionCategoryLabel,
  supportsWarehouseRegistry,
} from './connectionCategory';
import { WarehouseRegistrySection } from './WarehouseRegistrySection';
import {
  CatalogConnectorOverviewSection,
  CatalogCorrectionQueueSection,
} from './CatalogConnectionPanels';

interface ConnectionDetailPaneProps {
  connectorId: string | null;
  onDeleted?: () => void;
}

const RUN_STATUS_STYLES: Record<string, string> = {
  completed: 'bg-green-500/10 text-green-500',
  failed: 'bg-red-500/10 text-red-500',
  running: 'bg-eliza-red/10 text-eliza-red',
  pending: 'bg-gray-100 dark:bg-dark-surface-2 text-gray-500 dark:text-gray-400',
  cancelled: 'bg-amber-500/10 text-amber-500',
};

const OAUTH_POPUP_POLL_INTERVAL_MS = 1000;
const OAUTH_POPUP_CLOSED_GRACE_MS = 750;

export function ConnectionDetailPane({ connectorId, onDeleted }: ConnectionDetailPaneProps) {
  const queryClient = useQueryClient();
  const { push: addToast } = useToasts();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSyncConfig, setEditSyncConfig] = useState('');
  const [editBigQueryProjectId, setEditBigQueryProjectId] = useState('');
  const [editBigQueryClientId, setEditBigQueryClientId] = useState('');
  const [editBigQueryClientSecret, setEditBigQueryClientSecret] = useState('');
  const [editBigQueryRedirectUri, setEditBigQueryRedirectUri] = useState('');
  const [isStartingBigQueryOauth, setIsStartingBigQueryOauth] = useState(false);
  const [isStartingGoogleOauth, setIsStartingGoogleOauth] = useState(false);

  const connectorKey = connectorId || '';

  const { data: connector, isLoading: isConnectorLoading } = useGetConnector(connectorKey, {
    query: {
      enabled: !!connectorId,
      staleTime: 15000,
      refetchInterval: 30000,
    },
  });
  const syncFeaturesEnabled =
    !!connectorId &&
    !!connector &&
    getConnectorCatalogEntry(connector.connector_type).supportsManualSync;

  const { data: syncHistoryData, isLoading: isSyncHistoryLoading } = useListSyncRuns(
    { connector_id: connectorKey || undefined },
    { query: { enabled: syncFeaturesEnabled, staleTime: 10000 } }
  );

  const { data: statistics, isLoading: isStatisticsLoading } = useGetConnectorStatistics(connectorKey, {
    query: { enabled: syncFeaturesEnabled, staleTime: 20000 },
  });

  const { mutate: triggerSync, isPending: isSyncing } = useTriggerSync(connectorId ?? '', {
    mutation: {
      onSuccess: () => {
        addToast({ kind: 'success', message: 'Sync started successfully!' });
        if (connectorId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.detail(connectorId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.syncHistory(connectorId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.statistics(connectorId) });
        }
      },
      onError: (error: any) => {
        addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to start sync.' });
      },
    },
  });

  const { mutate: deleteConnector, isPending: isDeleting } = useDeleteConnector({
    mutation: {
      onSuccess: () => {
        addToast({ kind: 'success', message: 'Connection deleted successfully!' });
        if (connectorId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
        }
        onDeleted?.();
      },
      onError: (error: any) => {
        addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to delete connection.' });
      },
    },
  });
  const { mutate: updateConnector, isPending: isUpdating } = useUpdateConnector(connectorId ?? '', {
    mutation: {
      onSuccess: () => {
        addToast({ kind: 'success', message: 'Connection updated successfully!' });
        setIsEditOpen(false);
        if (connectorId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.detail(connectorId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
        }
      },
      onError: (error: any) => {
        addToast({ kind: 'error', message: error?.response?.data?.detail || 'Failed to update connection.' });
      },
    },
  });

  const recentRuns = useMemo(() => {
    const runs = syncHistoryData?.sync_runs ?? [];
    return runs.slice(0, 5);
  }, [syncHistoryData]);

  const connectorCatalog = connector ? getConnectorCatalogEntry(connector.connector_type) : null;
  const supportsManualSync = connectorCatalog?.supportsManualSync ?? true;
  const isLiveRetrievalOnly = Boolean(connector?.sync_config?.live_retrieval_only);
  const connectionScope = connector
    ? getConnectorScopeLabel(connector.sync_config)
    : 'Tenant-wide';
  const connectionSummary = connector
    ? getConnectorSummary(connector.connector_type, connector.sync_config)
    : null;
  const requiredPermissions = connector
    ? getConnectorRequiredPermissions(connector.sync_config)
    : [];
  const totalSyncRuns = statistics?.total_syncs ?? 0;
  const successfulSyncs = statistics?.successful_syncs ?? 0;
  const failedSyncs = statistics?.failed_syncs ?? 0;
  const recordsIngested = statistics?.total_records_synced ?? 0;
  const createdAt = parseApiDate(connector?.created_at);

  const connectionCategory = connector
    ? getDataConnectionCategory(connector.connector_type)
    : null;
  const bigQueryOauth =
    connector?.connector_type === 'bigquery' ? connector.bigquery_oauth : null;
  const isBigQueryConnector = connector?.connector_type === 'bigquery';
  const isGoogleWorkspaceConnector = ['google_drive', 'google_sheets', 'gmail'].includes(
    connector?.connector_type || '',
  );
  const bigQueryReadinessLabel =
    bigQueryOauth?.readiness_code === 'ready'
      ? 'Authorized'
      : bigQueryOauth?.readiness_code === 'env_fallback_configured'
        ? 'Using environment fallback'
        : bigQueryOauth?.configured
          ? 'Authorization required'
          : 'OAuth app missing';

  const handleTriggerSync = () => {
    if (!connector || !supportsManualSync) return;
    triggerSync({ sync_mode: connector.sync_mode });
  };

  const handleDelete = () => {
    if (!connector) return;
    if (window.confirm(`Delete connection "${connector.connector_name}"? This action cannot be undone.`)) {
      deleteConnector(connector.connector_id);
    }
  };
  const handleOpenEdit = () => {
    if (!connector) return;
    setEditName(connector.connector_name || '');
    setEditDescription(connector.description || '');
    setEditSyncConfig(JSON.stringify(connector.sync_config || {}, null, 2));
    const syncConfig = connector.sync_config as Record<string, unknown> | null | undefined;
    setEditBigQueryProjectId(
      String(connector.bigquery_oauth?.project_id || syncConfig?.project_id || syncConfig?.project || ''),
    );
    setEditBigQueryClientId('');
    setEditBigQueryClientSecret('');
    setEditBigQueryRedirectUri(
      String(connector.bigquery_oauth?.callback_uri || connector.bigquery_oauth?.redirect_uri || ''),
    );
    setIsEditOpen(true);
  };
  const handleSaveEdit = () => {
    if (!connector) return;
    let parsedSyncConfig: Record<string, any>;
    try {
      parsedSyncConfig = JSON.parse(editSyncConfig || '{}');
    } catch {
      addToast({ kind: 'error', message: 'Sync configuration must be valid JSON.' });
      return;
    }
    const payload: Record<string, any> = {
      connector_name: editName.trim() || connector.connector_name,
      description: editDescription.trim() || undefined,
      sync_config: parsedSyncConfig,
    };

    if (isBigQueryConnector) {
      const credentials: Record<string, string> = { auth_method: 'oauth' };
      const projectId = editBigQueryProjectId.trim();
      const clientId = editBigQueryClientId.trim();
      const clientSecret = editBigQueryClientSecret.trim();
      const redirectUri = editBigQueryRedirectUri.trim();

      if (projectId) credentials.project_id = projectId;
      if (clientId) credentials.client_id = clientId;
      if (clientSecret) credentials.client_secret = clientSecret;
      if (redirectUri) credentials.redirect_uri = redirectUri;

      if (Object.keys(credentials).length > 1) {
        payload.credentials = credentials;
      }
    }

    updateConnector(payload);
  };

  const handleBigQueryReauthorize = async () => {
    if (!connector || isStartingBigQueryOauth) return;
    setIsStartingBigQueryOauth(true);
    let popup: Window | null = null;
    let oauthCompleted = false;

    try {
      popup = window.open('about:blank', 'bigquery_oauth', 'width=640,height=760');
      if (!popup) {
        throw new Error('Popup blocked');
      }
      popup.document.write('<p style="font-family: sans-serif; padding: 16px;">Preparing Google OAuth...</p>');

      const { data } = await AXIOS_INSTANCE.get('/oauth/authorize/bigquery', {
        params: {
          connector_config_id: connector.id,
          mode: 'connector_setup',
          return_origin: window.location.origin,
        },
      });
      popup.location.href = data.authorize_url;

      const refreshBigQueryOauthStatus = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.connectors.detail(connector.connector_id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
      };

      const cleanup = () => {
        window.removeEventListener('message', handler);
        clearInterval(interval);
        setIsStartingBigQueryOauth(false);
      };

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'oauth_complete' && event.data?.source === 'bigquery') {
          oauthCompleted = true;
          cleanup();
          addToast({ kind: 'success', message: 'BigQuery OAuth setup completed.' });
          refreshBigQueryOauthStatus();
        }
      };

      window.addEventListener('message', handler);
      const interval = window.setInterval(() => {
        if (!popup?.closed || oauthCompleted) {
          return;
        }
        window.clearInterval(interval);
        window.setTimeout(() => {
          if (oauthCompleted) {
            return;
          }
          oauthCompleted = true;
          cleanup();
          refreshBigQueryOauthStatus();
        }, OAUTH_POPUP_CLOSED_GRACE_MS);
      }, OAUTH_POPUP_POLL_INTERVAL_MS);
    } catch (error: any) {
      if (popup && !popup.closed) {
        popup.close();
      }
      setIsStartingBigQueryOauth(false);
      addToast({
        kind: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to initiate BigQuery OAuth.',
      });
    }
  };

  const handleGoogleReauthorize = async () => {
    if (!connector || !isGoogleWorkspaceConnector || isStartingGoogleOauth) return;
    const popup = window.open('about:blank', 'google_workspace_oauth', 'width=640,height=760');
    if (!popup) {
      addToast({ kind: 'error', message: 'Popup blocked. Allow popups and try again.' });
      return;
    }

    setIsStartingGoogleOauth(true);
    popup.document.write('<p style="font-family: sans-serif; padding: 16px;">Preparing Google OAuth...</p>');
    let oauthCompleted = false;
    try {
      const authorization = await googleWorkspaceAuthorizeOauthAuthorizeGoogleGet({
        connector_config_id: connector.id,
        return_origin: window.location.origin,
      });
      popup.location.href = authorization.authorize_url;

      const refreshConnector = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.connectors.detail(connector.connector_id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.connectors.lists() });
      };
      const cleanup = () => {
        window.removeEventListener('message', handler);
        window.clearInterval(interval);
        setIsStartingGoogleOauth(false);
      };
      const handler = (event: MessageEvent) => {
        if (
          event.source === popup &&
          event.data?.type === 'oauth_complete' &&
          event.data?.source === connector.connector_type
        ) {
          oauthCompleted = true;
          cleanup();
          refreshConnector();
          addToast({ kind: 'success', message: 'Google connection authorized.' });
        }
      };

      window.addEventListener('message', handler);
      const interval = window.setInterval(() => {
        if (!popup.closed || oauthCompleted) return;
        cleanup();
        refreshConnector();
      }, OAUTH_POPUP_POLL_INTERVAL_MS);
    } catch (error: any) {
      if (!popup.closed) popup.close();
      setIsStartingGoogleOauth(false);
      addToast({
        kind: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to initiate Google OAuth.',
      });
    }
  };

  // Empty state
  if (!connectorId) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 dark:bg-dark-surface-2 mb-4">
          <LinkIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="text-base font-medium text-charcoal dark:text-gray-100">Select a connection</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
          Choose a connection from the list to view details and manage syncs.
        </p>
      </div>
    );
  }

  // Loading state
  if (isConnectorLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-eliza-red mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading connection details…</p>
      </div>
    );
  }

  // Error state
  if (!connector) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10 mb-4">
          <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-base font-medium text-charcoal dark:text-gray-100">Connection not found</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
          This connection may have been deleted or is no longer available.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-5 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-eliza-red/10">
              <LinkIcon className="h-6 w-6 text-eliza-red" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 truncate">
                {connector.connector_name}
              </h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-dark-surface-2 text-xs font-medium">
                  {getConnectorLabel(connector.connector_type)}
                </span>
                {connectionCategory ? (
                  <>
                    <span>•</span>
                    <span className="px-2 py-0.5 rounded-md border border-gray-200 text-xs font-medium dark:border-dark-border">
                      {getDataConnectionCategoryLabel(connectionCategory)} connection
                    </span>
                  </>
                ) : null}
                <span>•</span>
                <span>{connectionScope}</span>
                <span>•</span>
                <span>Updated {formatConnectorActivityTime(connector)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3 flex-shrink-0">
            <span className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
              !connector.is_enabled
                ? 'bg-gray-100 dark:bg-dark-surface-2 text-gray-500 dark:text-gray-400'
                : connector.is_healthy !== false
                ? 'bg-green-500/10 text-green-500'
                : 'bg-amber-500/10 text-amber-500'
            )}>
              {!connector.is_enabled ? (
                <ClockIcon className="h-3.5 w-3.5" />
              ) : connector.is_healthy !== false ? (
                <CheckCircleIcon className="h-3.5 w-3.5" />
              ) : (
                <ExclamationCircleIcon className="h-3.5 w-3.5" />
              )}
              {!connector.is_enabled
                ? 'Inactive'
                : connector.is_healthy !== false
                ? 'Active'
                : 'Reauth Required'}
            </span>
            <div className="flex items-center gap-2">
              {isGoogleWorkspaceConnector && (
                <button
                  onClick={handleGoogleReauthorize}
                  disabled={isStartingGoogleOauth}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 bg-eliza-red text-white hover:bg-eliza-red-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isStartingGoogleOauth ? (
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <LinkIcon className="h-3.5 w-3.5" />
                  )}
                  {isStartingGoogleOauth
                    ? 'Opening OAuth...'
                    : connector.is_healthy === false
                      ? 'Authorize Google'
                      : 'Reauthorize Google'}
                </button>
              )}
              {supportsManualSync && !isLiveRetrievalOnly && (
                <button
                  onClick={handleTriggerSync}
                  disabled={isSyncing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 bg-eliza-red text-white hover:bg-eliza-red-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSyncing ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                  Sync Now
                </button>
              )}
              <button
                onClick={handleOpenEdit}
                disabled={isUpdating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border bg-gray-50 dark:bg-dark-surface-2 text-charcoal dark:text-gray-100 border-gray-200 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-surface-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PencilIcon className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border bg-gray-50 dark:bg-dark-surface-2 text-red-500 border-gray-200 dark:border-dark-border hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-6 space-y-6">
          {connector.is_enabled && connector.is_healthy === false && (
            <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 text-amber-600 dark:text-amber-400 flex gap-3 items-start text-sm">
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800 dark:text-amber-300">Connection Health Warning</h4>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 opacity-90">
                  {connector.health_check_message || 'This data source is currently reporting authentication/connectivity issues. Chat queries might not return data from this source.'}
                </p>
                <p className="mt-2 text-xs font-medium">
                  Please click the Settings tab above and re-authorize or re-enter credentials for this connection.
                </p>
              </div>
            </div>
          )}
          {bigQueryOauth && (
            <section className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-medium text-charcoal dark:text-gray-100">
                    BigQuery OAuth Setup
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {bigQueryReadinessLabel}
                    {bigQueryOauth.uses_env_fallback ? ' from legacy environment configuration' : ''}
                  </p>
                </div>
                <span className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                  bigQueryOauth.has_setup_grant
                    ? 'bg-green-500/10 text-green-500'
                    : 'bg-amber-500/10 text-amber-500',
                )}>
                  {bigQueryOauth.has_setup_grant ? (
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                  ) : (
                    <ExclamationCircleIcon className="h-3.5 w-3.5" />
                  )}
                  {bigQueryOauth.has_setup_grant ? 'Setup grant stored' : 'Setup grant missing'}
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-1 gap-3 text-xs md:grid-cols-2">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Project ID</dt>
                  <dd className="mt-1 font-mono text-charcoal dark:text-gray-100 break-all">
                    {bigQueryOauth.project_id || 'Not configured'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Callback URI</dt>
                  <dd className="mt-1 font-mono text-charcoal dark:text-gray-100 break-all">
                    {bigQueryOauth.callback_uri || bigQueryOauth.redirect_uri || 'Not configured'}
                  </dd>
                </div>
              </dl>
              {!bigQueryOauth.uses_env_fallback && (
                <button
                  type="button"
                  onClick={handleBigQueryReauthorize}
                  disabled={isStartingBigQueryOauth}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-eliza-red px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-eliza-red-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isStartingBigQueryOauth ? (
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <LinkIcon className="h-3.5 w-3.5" />
                  )}
                  {isStartingBigQueryOauth
                    ? 'Opening OAuth...'
                    : bigQueryOauth.has_setup_grant
                      ? 'Reauthorize Google'
                      : 'Authorize Google'}
                </button>
              )}
            </section>
          )}
          {supportsWarehouseRegistry(connector.connector_type) && (
            <WarehouseRegistrySection
              connectorId={connector.id}
              connectorDisplayName={connector.connector_name}
              warehouseKindLabel={getConnectorLabel(connector.connector_type)}
              projectHint={
                String(connector.connector_type).toLowerCase() === 'bigquery'
                  ? (() => {
                      const cfg = connector.sync_config as Record<string, unknown> | null | undefined;
                      const project = String(cfg?.project_id || cfg?.project || '').trim();
                      return project || null;
                    })()
                  : null
              }
            />
          )}
          {connectionCategory === 'catalog' && (
            <>
              <CatalogConnectorOverviewSection
                connectorDisplayName={connector.connector_name}
                configId={connector.id}
              />
              <CatalogCorrectionQueueSection configId={connector.id} />
            </>
          )}

          {isLiveRetrievalOnly ? (
            <section>
              <div className="text-center py-8 px-4 border border-dashed border-gray-200 dark:border-dark-border rounded-xl">
                <LinkIcon className="h-6 w-6 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-charcoal dark:text-gray-100">Live Retrieval Source</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This source is queried in real time during conversations. No sync or ingestion is needed.
                </p>
              </div>
            </section>
          ) : supportsManualSync ? (
            <>
              {/* Metrics */}
              <section>
                <h3 className="text-sm font-medium text-charcoal dark:text-gray-100 mb-4 flex items-center gap-2">
                  <ChartBarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  Key Metrics
                </h3>
                {isStatisticsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-eliza-red" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard icon={<ChartBarIcon className="h-5 w-5" />} label="Total Syncs" value={totalSyncRuns} color="brand" />
                    <MetricCard icon={<CheckCircleIcon className="h-5 w-5" />} label="Successful" value={successfulSyncs} color="success" />
                    <MetricCard icon={<ExclamationCircleIcon className="h-5 w-5" />} label="Failed" value={failedSyncs} color="error" />
                    <MetricCard icon={<CloudArrowUpIcon className="h-5 w-5" />} label="Records" value={recordsIngested} color="info" />
                  </div>
                )}
              </section>

              {/* Recent Sync Runs */}
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-charcoal dark:text-gray-100 flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    Recent Sync Runs
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {isSyncHistoryLoading ? 'Loading…' : `${recentRuns.length} of ${(syncHistoryData?.sync_runs?.length ?? 0)} shown`}
                  </span>
                </div>

                {isSyncHistoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-eliza-red" />
                  </div>
                ) : recentRuns.length === 0 ? (
                  <div className="text-center py-8 px-4 border border-dashed border-gray-200 dark:border-dark-border rounded-xl">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No sync runs yet. Click "Sync Now" to start.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentRuns.map((run: any) => {
                      const statusStyle = RUN_STATUS_STYLES[run.status] || 'bg-gray-100 dark:bg-dark-surface-2 text-gray-500 dark:text-gray-400';
                      const startedAt = parseApiDate(run.started_at);
                      const completedAt = parseApiDate(run.completed_at);
                      const durationSeconds = startedAt && completedAt
                        ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
                        : null;
                      const errorCount = run.records_failed ?? run.error_count ?? 0;

                      return (
                        <div key={run.id} className="p-4 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <span className={cn('rounded-full px-2.5 py-1 text-xs font-medium uppercase', statusStyle)}>
                              {run.status}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatRelativeApiTime(run.started_at)}
                            </span>
                          </div>

                          <div className="grid grid-cols-4 gap-4 text-xs">
                            <div>
                              <p className="text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Extracted</p>
                              <p className="text-charcoal dark:text-gray-100 font-medium">{(run.records_extracted || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Loaded</p>
                              <p className="text-charcoal dark:text-gray-100 font-medium">{(run.records_loaded || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Skipped</p>
                              <p className="text-charcoal dark:text-gray-100 font-medium">{(run.records_skipped || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Errors</p>
                              <p className={cn('font-medium', errorCount > 0 ? 'text-red-500' : 'text-charcoal dark:text-gray-100')}>
                                {errorCount.toLocaleString()}
                              </p>
                            </div>
                          </div>

                          {durationSeconds !== null && (
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                              Duration: {durationSeconds}s • Mode: {run.sync_mode?.replace(/_/g, ' ') || '—'}
                            </p>
                          )}

                          {run.error_message && (
                            <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-500">
                              {run.error_message}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          ) : null}

          {/* Connection Details */}
          <section>
            <h3 className="text-sm font-medium text-charcoal dark:text-gray-100 mb-4 flex items-center gap-2">
              <Cog6ToothIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              Connection Details
            </h3>
            <div className="rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
              <dl className="divide-y divide-gray-200 dark:divide-dark-border">
                <DetailRow label="Type" value={getConnectorLabel(connector.connector_type)} />
                <DetailRow label="Scope" value={connectionScope} />
                {connectionSummary && <DetailRow label="Summary" value={connectionSummary} />}
                {requiredPermissions.length > 0 && (
                  <DetailRow label="Required Permissions" value={requiredPermissions.join(', ')} />
                )}
                {supportsManualSync && !isLiveRetrievalOnly && (
                  <DetailRow label="Sync Mode" value={connector.sync_mode?.replace(/_/g, ' ') || '—'} />
                )}
                <DetailRow label="Created" value={createdAt ? format(createdAt, 'MMM d, yyyy HH:mm') : '—'} />
                {connector.sync_schedule && (
                  <DetailRow label="Schedule" value={connector.sync_schedule} />
                )}
              </dl>
            </div>
          </section>

          {/* Description */}
          {connector.description && (
            <section>
              <h3 className="text-sm font-medium text-charcoal dark:text-gray-100 mb-3">Description</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{connector.description}</p>
            </section>
          )}

          {/* Configuration */}
          <section>
            <h3 className="text-sm font-medium text-charcoal dark:text-gray-100 mb-3">Raw Configuration</h3>
            <pre className="p-4 rounded-xl bg-gray-50 dark:bg-dark-surface-2 border border-gray-200 dark:border-dark-border text-xs text-gray-500 dark:text-gray-400 overflow-x-auto font-mono">
              {JSON.stringify(connector.sync_config, null, 2)}
            </pre>
          </section>
        </div>
      </div>
      {isEditOpen && connector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-3xl rounded-xl border border-gray-200 bg-white shadow-xl dark:border-dark-border dark:bg-dark-surface">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-dark-border">
              <div>
                <h3 className="text-base font-semibold text-charcoal dark:text-gray-100">Edit connection</h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Update the saved connection details and sync configuration.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-dark-surface-2"
                aria-label="Close edit connection"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-5">
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Connection name
                </span>
                <input
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Description
                </span>
                <textarea
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  rows={2}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                />
              </label>
              {isBigQueryConnector && (
                <section className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-charcoal dark:text-gray-100">
                      BigQuery OAuth app
                    </h4>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Update tenant-owned Google OAuth settings. Leave client ID or secret blank to keep the stored value.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Google Cloud project ID
                      </span>
                      <input
                        value={editBigQueryProjectId}
                        onChange={(event) => setEditBigQueryProjectId(event.target.value)}
                        placeholder="e.g., venetian-production"
                        className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        OAuth client ID
                      </span>
                      <input
                        value={editBigQueryClientId}
                        onChange={(event) => setEditBigQueryClientId(event.target.value)}
                        placeholder={
                          connector.bigquery_oauth?.has_client_id
                            ? 'Stored client ID configured; enter to replace'
                            : 'Client ID from Google Cloud'
                        }
                        className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        OAuth client secret
                      </span>
                      <input
                        type="password"
                        value={editBigQueryClientSecret}
                        onChange={(event) => setEditBigQueryClientSecret(event.target.value)}
                        placeholder={
                          connector.bigquery_oauth?.has_client_secret
                            ? 'Stored secret configured; enter to rotate'
                            : 'Client secret from Google Cloud'
                        }
                        className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Redirect URI
                      </span>
                      <input
                        value={editBigQueryRedirectUri}
                        onChange={(event) => setEditBigQueryRedirectUri(event.target.value)}
                        placeholder="https://your-backend.example.com/oauth/callback/bigquery"
                        className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
                      />
                    </label>
                  </div>
                  {(connector.bigquery_oauth?.callback_uri || connector.bigquery_oauth?.redirect_uri) && (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-dark-border dark:bg-dark-surface">
                      <p className="font-medium text-charcoal dark:text-gray-100">Current callback URI</p>
                      <p className="mt-1 break-all font-mono text-gray-500 dark:text-gray-400">
                        {connector.bigquery_oauth.callback_uri || connector.bigquery_oauth.redirect_uri}
                      </p>
                    </div>
                  )}
                </section>
              )}
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Sync configuration JSON
                </span>
                <textarea
                  value={editSyncConfig}
                  onChange={(event) => setEditSyncConfig(event.target.value)}
                  rows={10}
                  spellCheck={false}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/20 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-dark-border">
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-charcoal transition hover:bg-gray-50 dark:border-dark-border dark:text-gray-100 dark:hover:bg-dark-surface-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isUpdating}
                className="rounded-lg bg-eliza-red px-4 py-2 text-sm font-medium text-white transition hover:bg-eliza-red-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isUpdating ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'brand' | 'success' | 'error' | 'info';
}

function MetricCard({ icon, label, value, color }: MetricCardProps) {
  const colorClasses = {
    brand: 'bg-eliza-red/10 text-eliza-red',
    success: 'bg-green-500/10 text-green-500',
    error: 'bg-red-500/10 text-red-500',
    info: 'bg-blue-500/10 text-blue-500',
  };

  return (
    <div className="p-4 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface">
      <div className={cn('inline-flex h-10 w-10 items-center justify-center rounded-lg mb-3', colorClasses[color])}>
        {icon}
      </div>
      <p className="text-2xl font-semibold text-charcoal dark:text-gray-100">{value.toLocaleString()}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-charcoal dark:text-gray-100 font-medium">{value}</dd>
    </div>
  );
}

export default ConnectionDetailPane;
